import { makeAutoObservable, reaction } from 'mobx';
import { RootStore } from './rootStore';
import { Rate, Room, getRooms } from '../apiMethods/getRooms';
import { addDays, differenceInCalendarDays, format, startOfDay } from 'date-fns';
import cloneDeep from 'lodash/cloneDeep';
import { isEqual } from 'lodash';

export type RateMode = 'CORP' | 'SPEC' | 'OPEN';

type GuestRoom = {
  id: string;
  params: {
    adults: number;
    childs: {
      id: number;
      count: number;
      code: string;
      use_in_calc: boolean;
    }[];
  };
};

const today = startOfDay(new Date());

export class ParamsStore {
  root: RootStore;
  constructor(rootStore: RootStore) {
    makeAutoObservable(this);

    this.root = rootStore;
  }

  disposers: (() => void)[] = [];

  dispose() {
    this.disposers.forEach((f) => f());
  }

  init() {
    this.disposers = [
      reaction(() => Boolean(this.root.authStore.isCompanyEmployee), () => {
        if (this.root.authStore.isCompanyEmployee) {
          this.setRateMode('CORP')
        } else {
          this.setRateMode('OPEN')
        }
      })
    ];
  }

  range:
    | {
        from?: Date;
        to?: Date;
      }
    | undefined = { from: today, to: addDays(today, 1) };

  setRange(newRange?: { from?: Date; to?: Date }) {
    this.range = newRange;
  }

  get from() {
    return this.appliedParams?.range?.from;
  }
  get to() {
    return this.appliedParams?.range?.to;
  }

  get nightsCount() {
    if (!this.to || !this.from) {
      return 0;
    }

    return differenceInCalendarDays(this.to, this.from);
  }

  promocode?: string;

  setPromocode(newPromocode?: string) {
    this.promocode = newPromocode;
  }

  rateMode?: RateMode = 'OPEN';

  setRateMode(rateMode: typeof this.rateMode) {
    this.rateMode = rateMode || 'OPEN';
  }

  guestRooms: GuestRoom[] = [];

  setGuestRooms(guestRooms: typeof this.guestRooms) {
    this.guestRooms = guestRooms;
  }

  countGuests(guestRooms: GuestRoom[]) {
    if (!guestRooms) {
      return [0, 0] as const;
    }
    const adults = guestRooms.reduce((sum, cur) => sum + cur.params.adults, 0);

    let childs = 0;
    for (const gr of guestRooms) {
      const count = gr.params.childs.reduce((sum, cur) => sum + cur.count, 0);
      childs += count;
    }
    return [adults, childs] as const;
  }

  get roomsCount() {
    return this?.appliedParams?.guestRooms.length;
  }

  get searchParams() {
    return {
      range: this.range,
      promocode: this.promocode,
      rateMode: this.rateMode,
      guestRooms: this.guestRooms,
    };
  }

  appliedSearchParams: null | {
    range: ParamsStore['range'];
    promocode: ParamsStore['promocode'];
    rateMode: ParamsStore['rateMode'];
    guestRooms: ParamsStore['guestRooms'];
  } = null;

  setAppliedParams(params: typeof this.appliedSearchParams) {
    this.appliedSearchParams = cloneDeep(params);
  }

  get appliedParams() {
    return this.appliedSearchParams;
  }

  get hasUnappliedParams() {
    const {guestRooms, ...currentParams } = this.searchParams;

    const { guestRooms: appliedGuestRooms, ...appliedParams } = this.appliedSearchParams || {};
    const equal = isEqual(currentParams, appliedParams);

    // rooms compared separately since each room object has a unique id
    const areRoomsEqual = isEqual(
      guestRooms.flatMap((r) => r.params),
      appliedGuestRooms?.flatMap((r) => r.params),
    );

    return !(equal && areRoomsEqual);
  }

  async loadRooms() {
    this.setAppliedParams(this.searchParams);

    const hotelId = this.root.configStore.hotelId;
    if (!hotelId) {
      // TODO
      throw new Error('hotelId not found');
    }

    if (!this.range?.from || !this.range?.to) {
      return;
    }

    const bodyRequest = {
      arrival: format(this.range.from, 'yyyy-MM-dd'),
      departure: format(this.range.to, 'yyyy-MM-dd'),
      promo_code: this.promocode || '',
      rooms: this.guestRooms.map((x) => ({
        ...x.params,
        childs: x.params.childs.map((c) => ({ ...c, code: 'CODE' })),
      })),
      rate_flow: this.rateMode || '',
    };

    this.roomsPending = true;
    const rooms = await getRooms(hotelId, bodyRequest);

    rooms && this.setRooms(rooms);
    this.roomsPending = false;
  }

  rooms: Room[] = [];

  setRooms(rooms: Room[]) {
    this.rooms = rooms;
  }

  roomsPending = false;
  setRoomsPending(pending: boolean) {
    this.roomsPending = pending;
  }

  get roomCategories() {
    const categories = [...new Set(this.rooms.map((room) => room.category).filter((x) => x))];
    if (!categories.length) {
      return [];
    }
    return ['Все номера', ...categories];
  }

  getRoomsByCategory() {
    if (!this.category || this.category === 'Все номера') {
      return this.rooms;
    }
    return this.rooms.filter((room) => room.category === this.category);
  }

  category: string | null = 'Все номера';

  selectCategory(category: string | null) {
    this.category = category;
  }

  getRoom(roomId: number) {
    const room = this.rooms.find((room) => room.id === roomId);

    return room || null;
  }

  room: Room | null = null;

  selectRoom(roomId: number) {
    const room = this.getRoom(roomId);
    this.room = room || null;
  }

  rate: Rate | null = null;

  selectRate(rateId: number | null) {
    const rate = this.room?.rates.find((rate) => rate.id === rateId);
    this.rate = rate || null;
  }

  get totalPrice() {
    if (!this.rate) {
      return null;
    }
    return this.rate?.total_price;
  }

  guest?: {
    first_name: string;
    last_name: string;
    middle_name?: string;
    phone: string;
    email: string;
  };

  setGuest(guest: typeof this.guest) {
    this.guest = guest;
  }

  payment:
    | {
        id: number;
        payment_type: string;
        payment_alias: string;
        part: null | number;
        is_not_pay: boolean;
        hotel: number;
      }
    | undefined;

  setPayment(payment: typeof this.payment) {
    this.payment = payment;
  }

  get bookingParams() {
    const { range, promocode, rateMode, guestRooms } = this.appliedSearchParams || {};
    const { from, to } = range || {};

    const { room, rate, payment } = this;
    if (!from || !to || !room || !rate || !rateMode || !payment || !guestRooms) {
      return null;
    }

    return {
      from,
      to,
      room,
      rate,
      promocode,
      rateMode,
      guestRooms,
      payment,
    };
  }
}
